import { useCallback, useEffect, useRef, useState } from 'react'
import type { BrowserClientMessage, BrowserState } from '@hub/contracts'
import { BrowserClient, type BrowserConnection } from './browser-client'

/**
 * React binding for a thread's BrowserSession socket (PLAN-083). Owned by
 * the workspace sidebar so both the header actions and the view share one
 * connection and one `expanded` flag.
 */
export interface BrowserSession {
  state: BrowserState | null
  connection: BrowserConnection
  /** Last socket error, cleared after a few seconds. */
  error: string | null
  send: (msg: BrowserClientMessage) => void
  expanded: boolean
  setExpanded: (expanded: boolean) => void
  /** Derived: the session is running (not idle/closed). */
  running: boolean
  /** Derived: the user is driving (agent tools paused). */
  userControls: boolean
}

export function useBrowserSession(projectId: string, threadId: string): BrowserSession {
  const clientRef = useRef<BrowserClient | null>(null)
  const [state, setState] = useState<BrowserState | null>(null)
  const [connection, setConnection] = useState<BrowserConnection>('connecting')
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    const client = new BrowserClient(projectId, threadId)
    clientRef.current = client
    const offs = [
      client.on('state', setState),
      client.on('connection', (status) => {
        setConnection(status)
        // Ask for a Live View URL as soon as we are connected; the DO mints
        // one per hour for a running session and reuses it otherwise.
        if (status === 'connected') client.send({ t: 'open' })
      }),
      client.on('error', (message) => {
        setError(message)
        setTimeout(() => setError(null), 5000)
      }),
    ]
    return () => {
      for (const off of offs) off()
      client.close()
      clientRef.current = null
      setState(null)
      setExpanded(false)
    }
  }, [projectId, threadId])

  // A session that (re)started after a close has no Live View yet — ask for one.
  useEffect(() => {
    if (state?.status === 'ready' && !state.liveView) clientRef.current?.send({ t: 'open' })
  }, [state?.status, state?.liveView])

  // Refresh the Live View URL before it expires.
  useEffect(() => {
    const expiresAt = state?.liveView?.expiresAt
    if (!expiresAt || state?.status === 'closed') return
    const ms = Date.parse(expiresAt) - Date.now() - 4 * 60 * 1000
    const timer = setTimeout(() => clientRef.current?.send({ t: 'open' }), Math.max(ms, 5_000))
    return () => clearTimeout(timer)
  }, [state?.liveView?.expiresAt, state?.status])

  // Leave the overlay when the session closes.
  useEffect(() => {
    if (state?.status === 'closed') setExpanded(false)
  }, [state?.status])

  const send = useCallback((msg: BrowserClientMessage) => clientRef.current?.send(msg), [])

  const status = state?.status ?? 'idle'
  return {
    state,
    connection,
    error,
    send,
    expanded,
    setExpanded,
    running: status === 'ready' || status === 'starting' || status === 'error',
    userControls: state?.control === 'user',
  }
}
